import { api } from "@src/app/api";
import { fetchCurrentTimezone, fetchYourShiftsByDateAggregated } from "@src/app/dayView/api";
import { ISharedShiftSlot } from "@src/app/dayView/constants";
import { FCMRequestData } from "@src/app/pendingSentHomeRequests/interfaces";
import { fetchAgentUnverifiedShifts } from "@src/app/store/ongoingShifts/apiV2";
import { addVerificationPreferencesToShiftFacility } from "@src/app/utils/shiftHelper";
import { logError } from "@src/appV2/lib/analytics";
import { Shift } from "@src/lib/interface";
import { FetchYourShiftsParams } from "@store/shift/api";
import moment from "moment-timezone";
import { Dispatch } from "redux";

import { ActionType, OpenShiftItem, ShiftAction } from "./model";

const groupByShiftName = (shift: Shift): string => {
  return shift.name as string;
};

const updateundoShiftActions = (dispatch: Dispatch, undoShiftActions: any) => {
  localStorage.setItem("isSignup", "true");
  dispatch({
    type: ActionType.UNDO_INTEREST,
    data: undoShiftActions,
  });
};

const setShowToast =
  (toastName = "") =>
  (dispatch: Dispatch) => {
    dispatch({
      type: ActionType.SET_SHOW_TOAST,
      data: { showToast: toastName },
    });
  };

const updateSharedShiftSlot = (sharedShiftSlot: ISharedShiftSlot | null) => ({
  type: ActionType.UPDATE_SHARED_SHIFT_SLOT,
  data: {
    sharedShiftSlot,
  },
});
const removeSharedShiftSlot = () => ({
  type: ActionType.UPDATE_SHARED_SHIFT_SLOT,
  data: null,
});

const getMyShifts =
  <T extends FetchYourShiftsParams>(params: T) =>
  async (dispatch) => {
    try {
      // TODO: After CH-17726, Uncomment the next line and remove the line after.
      // const myShifts = await fetchYourShifts({ ...params, groupByDate: true });
      const myShifts = await fetchYourShiftsByDateAggregated(params.dateFilter);
      dispatch({
        type: ActionType.GET_MY_SHIFTS_GROUPED_BY_DATE,
        data: { myShifts },
      });
      return myShifts;
    } catch (e) {
      logFailureEvent(ActionType.GET_MY_SHIFTS_GROUPED_BY_DATE, e);
    }
  };
const getAgentUnVerifiedShifts = () => async (dispatch) => {
  try {
    await fetchCurrentTimezone();
    const _unverifiedShifts = await fetchAgentUnverifiedShifts();
    const facilityIds = _unverifiedShifts.map((shift) => shift.facility?.userId).filter(Boolean);
    let unverifiedShifts = _unverifiedShifts.sort(
      (a, b) => moment(b.start).valueOf() - moment(a.start).valueOf()
    );
    if (facilityIds.length) {
      const { data: facilitiesPreferences } =
        await api.facility.fetchFacilitiesVerificationPreferences(facilityIds as string[]);
      unverifiedShifts = addVerificationPreferencesToShiftFacility(
        unverifiedShifts,
        facilitiesPreferences
      );
    }
    dispatch({
      type: ActionType.GET_UNVERIFIED_AGENT_SHIFTS,
      data: {
        unverifiedShifts,
      },
    });
  } catch (err) {
    logFailureEvent(ActionType.GET_UNVERIFIED_AGENT_SHIFTS, err);
  }
};

const setFcmRequestsData = (fcmRequestsData: FCMRequestData[]) => ({
  type: ActionType.SET_FCM_REQUESTS,
  data: {
    fcmRequestsData,
  },
});

const logFailureEvent = (type: string, error: Error) => (dispatch, getState) => {
  logError("Error while fetching shifts", { error });
  dispatch({
    type: `${type}_FAILURE`,
    data: { error },
  });
};

/**
 * Adds an open shift item to the shiftStore > OpenShiftItems state.
 * Open shift items are used to track state of shift item cards.
 */
const addOpenShiftItem = (openShiftItem: OpenShiftItem): ShiftAction => ({
  type: ActionType.ADD_OPEN_SHIFT_ITEM,
  data: {
    openShiftItem,
  },
});

/**
 * Updates an open shift item in the shiftStore > openShiftItems state.
 * Open shift items are used to track state of shift item cards.
 */
const updateOpenShiftItem = (openShiftItem: OpenShiftItem): ShiftAction => ({
  type: ActionType.UPDATE_OPEN_SHIFT_ITEM,
  data: {
    openShiftItem,
  },
});

/**
 * Clears the state of all open shift item cards. Helpful to refresh state,
 * for example when re-entering a shift list view.
 * Open shift items are used to track state of shift item cards.
 */
const clearOpenShiftItems = (): ShiftAction => ({
  type: ActionType.CLEAR_OPEN_SHIFT_ITEMS,
});

export {
  groupByShiftName,
  updateundoShiftActions,
  setShowToast,
  updateSharedShiftSlot,
  removeSharedShiftSlot,
  getAgentUnVerifiedShifts,
  getMyShifts,
  setFcmRequestsData,
  addOpenShiftItem,
  updateOpenShiftItem,
  clearOpenShiftItems,
};
